import { useHeaderButton } from "@/contexts/header-button-context";
import { GroupForm } from "../components/approval-group-form";
import { GroupUsersList } from "../components/approval-group-users";
import {
  GroupFormProvider,
  useApprovalGroupForm,
} from "../components/approval-group-context";
import { useEffect, useCallback, useMemo, useState } from "react";
import {
  Avatar,
  AvatarFallback,
  Button,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { useNavigate } from "react-router";
import { cn } from "@/utils/theme";
import type {
  ApprovalGroupFormData,
  ApprovalUser,
  SeniorityValues,
} from "@/types/entities/approval-groups";
import {
  useCreateApprovalGroup,
  useCreateGroupWorkload,
  useSyncMembersToGroup,
} from "@/queries/approval-groups";
import { AxiosError } from "axios";
import { isAPIError } from "@/utils/errors";

const getSeniorityValue = (level?: string): number => {
  if (!level) return 1;
  switch (level) {
    case "junior":
      return 4;
    case "medio":
      return 3;
    case "senior":
      return 2;
    case "lider":
    default:
      return 1;
  }
};

const CreateApprovalGroupContent = () => {
  const { setComp } = useHeaderButton();
  const navigate = useNavigate();
  const { canSave, isSubmitting } = useApprovalGroupForm();

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const buttonElement = useMemo(
    () => (
      <>
        <Button variant="quiet" size="xs" type="button" onClick={handleCancel}>
          Cancelar
          <Icon type="far" name="times" />
        </Button>
        <Button
          variant="default"
          size="xs"
          form="new-group-form"
          type="submit"
          disabled={!canSave}
        >
          Crear Grupo
          <Icon
            type="fas"
            name={isSubmitting ? "spinner-third" : "plus"}
            className={cn("text-current", isSubmitting && "animate-spin")}
          />
        </Button>
      </>
    ),
    [handleCancel, canSave, isSubmitting],
  );

  useEffect(() => {
    setComp(buttonElement);
    return () => {
      setComp(null);
    };
  }, [setComp, buttonElement]);

  return (
    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-8">
      <GroupForm />
      <GroupUsersList />
    </div>
  );
};

export const CreateApprovalGroup = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const clearErrors = useCallback(() => setApiErrors({}), []);

  const { mutateAsync: createGroup } = useCreateApprovalGroup();
  const { mutateAsync: syncMembers } = useSyncMembersToGroup("");
  const { mutateAsync: createWorkload } = useCreateGroupWorkload();

  const handleCreateGroup = useCallback(
    async (
      data: ApprovalGroupFormData,
      users: ApprovalUser[],
      seniorityValues: SeniorityValues,
    ) => {
      try {
        setApiErrors({});
        clearErrors();

        // check if theres is more than one senior or leader
        const leaderCount = users.filter(
          (user) => user.level === "lider",
        ).length;
        // const seniorCount = users.filter(
        //   (user) => user.level === "senior",
        // ).length;

        if (leaderCount > 1) {
          showToast({
            title: "Error al actualizar grupo",
            description:
              "Solo puede haber un Líder por grupo. Por favor, ajuste los niveles de los usuarios seleccionados.",
            options: {
              type: "error",
              duration: 5000,
            },
          });
          return;
        }

        // if (seniorCount > 1) {
        //   showToast({
        //     title: "Error al actualizar grupo",
        //     description:
        //       "Solo puede haber un Supervisor por grupo. Por favor, ajuste los niveles de los usuarios seleccionados.",
        //     options: {
        //       type: "error",
        //       duration: 5000,
        //     },
        //   });
        //   return;
        // }

        const groupData = { ...data, type: "approval" };
        const response = await createGroup(groupData);
        // If users are selected, sync them to the newly created group
        if (users && users.length > 0 && response?.data?.id) {
          await syncMembers({
            members: users.map(({ id, level }) => {
              const value = getSeniorityValue(level);
              return {
                id,
                level: value,
                priority: value,
                seniority: value,
              };
            }),
            groupId: response.data.id,
          });

          await createWorkload({
            id: response?.data?.id,
            seniorityValues,
          });
        }

        showToast({
          title: "Grupo creado",
          description:
            "Los datos del grupo y/o sus miembros han sido creados exitosamente.",
          options: {
            type: "success",
            duration: 5000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="success" shape="square" type="icon">
                  <Icon type="fas" name="save" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
        navigate(-1);
      } catch (error) {
        console.error("Error creating group:", error);
        if (error instanceof AxiosError) {
          if (isAPIError(error?.response?.data, "name", "validation.unique")) {
            setApiErrors({ name: "Este nombre ya está en uso." });
          }
        }
        showToast({
          title: "Error al crear grupo",
          description: "No se pudo crear el grupo. Inténtalo de nuevo.",
          options: {
            type: "error",
            duration: 5000,
          },
        });
      }
    },
    [
      createGroup,
      syncMembers,
      showToast,
      navigate,
      clearErrors,
      createWorkload,
    ],
  );

  return (
    <GroupFormProvider
      initValues={{
        formDataState: { name: "", description: "", type: "approval" },
        selectedUsers: [],
      }}
      onSave={handleCreateGroup}
      apiErrors={apiErrors}
      clearApiErrors={clearErrors}
    >
      <CreateApprovalGroupContent />
    </GroupFormProvider>
  );
};
